/**
 * ClinicalWorkspace — production-ready Posturra in-session surface.
 *
 * Renders the Mission-Control / Treatment-GPS / Outcome-Tracker hybrid in a
 * single screen. Replaces the old BodyDiagram inside DiagramSection (and is
 * shown side-by-side with the existing diagram during phased rollout).
 *
 * Props (all optional with sane defaults for the design gallery):
 *   client            — { name, sex }
 *   sessionMeta       — { num, totalSessions, date }
 *   stats             — { pain, rom, posture }            // current vs intake
 *   analysis          — { focus_muscles[] }               // from /posture/analyze
 *   treatmentMap      — { primary[], secondary[], related[], rationale, confidence }
 *   workedAreas       — string[]                          // therapist-marked muscles
 *   setWorkedAreas    — fn(next)                          // toggle worked-area
 *   onMuscleSelect    — fn(id)                            // opens the AnatomyInfoPanel from parent (Learning mode)
 *   previousFindings  — [{ id, label, history[5], detail:{perSession:[{num,pain,rom,note}]} }]
 *   newSinceLastVisit — { improved[], stable[], worse[], clientReported[], romChanges[] }
 *   treatmentLogic    — { primaryComplaint, chronicPriority, newFinding, compensation }
 *   onStartPlan       — fn()                              // primary CTA handler
 *
 * Performance:
 *   • Heavy sub-trees memoized (React.memo)
 *   • Only the *current* anatomy view is mounted; others lazy-load on tab switch
 *   • Mode/view/sex toggles use useCallback so children don't rerender
 */
import React, { memo, useCallback, useMemo, useState } from "react";
import {
  Sparkles, Activity, Timer, Target, BookOpen, ChevronRight, ChevronDown,
  Info, Play, Stethoscope, GraduationCap, User, X, Check, TrendingDown,
  TrendingUp, AlertCircle, PauseCircle,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────
// Static asset paths (webp — ~25KB each)
// ─────────────────────────────────────────────────────────────────────────
const ANATOMY = {
  m: { front: "/anatomy/m_front.webp", back: "/anatomy/m_back.webp", side_l: "/anatomy/m_side_l.webp", side_r: "/anatomy/m_side_r.webp" },
  f: { front: "/anatomy/f_front.webp", back: "/anatomy/f_back.webp", side_l: "/anatomy/f_side_l.webp", side_r: "/anatomy/f_side_r.webp" },
};
// Fallback to female assets when sex is unspecified (neutral clinical default)
const sexKey = (sex) => (sex === "male" || sex === "m") ? "m" : "f";

// ─────────────────────────────────────────────────────────────────────────
// Hotspot position map: maps muscle-id (MUSCLE_LABELS keys) → {view, x, y, size}
// Hotspots are deliberately larger than v2 for visibility (size +20%).
// Calibrated against the cropped portrait anatomy images (1:2 aspect).
// ─────────────────────────────────────────────────────────────────────────
export const MUSCLE_POSITION_MAP = {
  // Anterior view
  temporalis:           { view: "front", x: 42, y: 6,  size: 4 },
  masseter:             { view: "front", x: 44, y: 9,  size: 4 },
  scm:                  { view: "front", x: 50, y: 13, size: 6 },
  scalenes:             { view: "front", x: 50, y: 14, size: 5 },
  anterior_deltoid:     { view: "front", x: 32, y: 19, size: 7 },
  lateral_deltoid:      { view: "front", x: 25, y: 21, size: 7 },
  pec_major_clavicular: { view: "front", x: 40, y: 21, size: 8 },
  pec_major_sternal:    { view: "front", x: 40, y: 25, size: 9 },
  pec_minor:            { view: "front", x: 40, y: 23, size: 7 },
  serratus_anterior:    { view: "front", x: 30, y: 30, size: 6 },
  biceps_brachii:       { view: "front", x: 22, y: 30, size: 7 },
  brachialis:           { view: "front", x: 21, y: 36, size: 5 },
  brachioradialis:      { view: "front", x: 20, y: 41, size: 5 },
  wrist_flexors:        { view: "front", x: 19, y: 47, size: 5 },
  rectus_abdominis_upper:{view: "front", x: 50, y: 32, size: 7 },
  rectus_abdominis_lower:{view: "front", x: 50, y: 40, size: 7 },
  external_obliques:    { view: "front", x: 38, y: 37, size: 6 },
  psoas_iliacus:        { view: "front", x: 44, y: 52, size: 6 },
  tfl:                  { view: "front", x: 33, y: 52, size: 5 },
  sartorius:            { view: "front", x: 43, y: 56, size: 6 },
  rectus_femoris:       { view: "front", x: 45, y: 60, size: 8 },
  vastus_lateralis:     { view: "front", x: 35, y: 58, size: 7 },
  vastus_medialis:      { view: "front", x: 45, y: 66, size: 6 },
  adductor_longus:      { view: "front", x: 47, y: 54, size: 5 },
  adductor_magnus:      { view: "front", x: 48, y: 58, size: 6 },
  gracilis:             { view: "front", x: 48, y: 62, size: 5 },
  tibialis_anterior:    { view: "front", x: 46, y: 80, size: 6 },
  peroneals:            { view: "front", x: 40, y: 80, size: 5 },
  // Posterior view
  occipitalis:          { view: "back", x: 50, y: 8,  size: 5 },
  occiput:              { view: "back", x: 50, y: 9,  size: 6 },  // alias used by hotspot demo
  suboccipitals:        { view: "back", x: 50, y: 12, size: 5 },
  splenius_capitis:     { view: "back", x: 50, y: 13, size: 6 },
  splenius_cervicis:    { view: "back", x: 45, y: 16, size: 4 }, // posterolateral cervical
  semispinalis_capitis: { view: "back", x: 53, y: 14, size: 5 }, // paramedian posterior neck
  semispinalis_cervicis:{ view: "back", x: 47, y: 17, size: 4 }, // deep lower-cervical paramedian
  multifidus_cervicis:  { view: "back", x: 50, y: 15, size: 5 }, // deep central cervical paraspinal
  upper_trapezius:      { view: "back", x: 50, y: 17, size: 13 },
  upper_trap:           { view: "back", x: 50, y: 17, size: 13 }, // alias
  middle_trapezius:     { view: "back", x: 50, y: 24, size: 11 },
  lower_trapezius:      { view: "back", x: 50, y: 30, size: 10 },
  rhomboids:            { view: "back", x: 50, y: 25, size: 11 },
  levator_scapulae:     { view: "back", x: 42, y: 17, size: 6 },
  posterior_deltoid:    { view: "back", x: 25, y: 21, size: 7 },
  supraspinatus:        { view: "back", x: 38, y: 19, size: 5 },
  infraspinatus:        { view: "back", x: 36, y: 23, size: 7 },
  teres_minor:          { view: "back", x: 32, y: 25, size: 5 },
  teres_major:          { view: "back", x: 32, y: 28, size: 6 },
  lats_upper:           { view: "back", x: 32, y: 30, size: 8 },
  lats_lower:           { view: "back", x: 36, y: 36, size: 7 },
  erector_spinae:       { view: "back", x: 50, y: 36, size: 9 },
  erector_t:            { view: "back", x: 50, y: 33, size: 9 }, // alias
  quadratus_lumborum:   { view: "back", x: 56, y: 44, size: 6 },
  ql:                   { view: "back", x: 56, y: 44, size: 6 }, // alias
  thoracolumbar_fascia: { view: "back", x: 50, y: 43, size: 6 },
  triceps_long:         { view: "back", x: 22, y: 30, size: 7 },
  triceps_lateral:      { view: "back", x: 19, y: 32, size: 6 },
  wrist_extensors:      { view: "back", x: 19, y: 46, size: 6 },
  gluteus_maximus:      { view: "back", x: 41, y: 55, size: 10 },
  gluteus_medius:       { view: "back", x: 35, y: 49, size: 6 },
  piriformis:           { view: "back", x: 43, y: 53, size: 5 },
  biceps_femoris:       { view: "back", x: 38, y: 65, size: 8 },
  semitendinosus:       { view: "back", x: 47, y: 65, size: 7 },
  semimembranosus:      { view: "back", x: 52, y: 66, size: 6 },
  IT_band:              { view: "back", x: 31, y: 62, size: 5 },
  gastrocnemius_medial: { view: "back", x: 48, y: 80, size: 6 },
  gastrocnemius_lateral:{ view: "back", x: 40, y: 80, size: 6 },
  soleus:               { view: "back", x: 45, y: 88, size: 6 },
  // Lateral aliases
  hip_flexor:           { view: "side_l", x: 50, y: 47, size: 7 },
  thoracic:             { view: "side_l", x: 56, y: 35, size: 8 },
};

// ─────────────────────────────────────────────────────────────────────────
// Lateral hotspot map — visually calibrated against the clean nose→right
// lateral figure (side_r). The Left lateral (side_l) figure is an exact
// horizontal mirror of the right, so left coords are derived as x → 100 - x.
// Only structures that are genuinely representable from a true lateral view
// are included; medial / far-side structures (medial gastrocnemius, the medial
// hamstrings, vastus medialis, adductors, anterior-midline abdominals, deep
// anterior hip flexors, and scapular/rotator-cuff/mid-back structures) are
// intentionally omitted so no misleading side markers are produced.
// ─────────────────────────────────────────────────────────────────────────
export const LATERAL_POSITION_MAP = {
  scm:                   { x: 54, y: 13, size: 5 },
  upper_trapezius:       { x: 47, y: 16, size: 7 },
  anterior_deltoid:      { x: 58, y: 20, size: 6 },
  lateral_deltoid:       { x: 52, y: 19, size: 7 },
  posterior_deltoid:     { x: 44, y: 20, size: 6 },
  pec_major_sternal:     { x: 58, y: 25, size: 7 },
  serratus_anterior:     { x: 54, y: 29, size: 6 },
  external_obliques:     { x: 56, y: 36, size: 6 },
  lats_upper:            { x: 44, y: 30, size: 7 },
  lats_lower:            { x: 46, y: 35, size: 6 },
  erector_spinae:        { x: 41, y: 34, size: 6 },
  thoracolumbar_fascia:  { x: 43, y: 42, size: 6 },
  quadratus_lumborum:    { x: 46, y: 43, size: 6 }, // deep — posterolateral flank projection
  gluteus_medius:        { x: 46, y: 48, size: 6 },
  tfl:                   { x: 52, y: 50, size: 5 },
  gluteus_maximus:       { x: 40, y: 52, size: 8 },
  piriformis:            { x: 43, y: 51, size: 5 }, // deep — mid-buttock projection (side-lying access)
  IT_band:               { x: 50, y: 62, size: 6 },
  vastus_lateralis:      { x: 54, y: 60, size: 7 },
  rectus_femoris:        { x: 58, y: 60, size: 6 },
  biceps_femoris:        { x: 44, y: 63, size: 7 }, // lateral hamstring
  gastrocnemius_lateral: { x: 46, y: 82, size: 6 },
  soleus:                { x: 47, y: 88, size: 5 },
  peroneals:             { x: 50, y: 86, size: 5 },
  tibialis_anterior:     { x: 56, y: 84, size: 5 },
};

// Resolve a lateral hotspot for the requested lateral view.
// side_r uses calibrated coords as-is; side_l is the exact horizontal mirror.
const lateralPos = (id, view) => {
  const p = LATERAL_POSITION_MAP[id];
  if (!p) return null;
  if (view === "side_r") return { x: p.x, y: p.y, size: p.size };
  if (view === "side_l") return { x: 100 - p.x, y: p.y, size: p.size };
  return null;
};

// ─────────────────────────────────────────────────────────────────────────
// Color tokens
// ─────────────────────────────────────────────────────────────────────────
const LEVEL = {
  primary:   { hex: "#ef4444", glow: "rgba(239,68,68,0.6)",  dot: "bg-red-500",     text: "text-red-300",     bg: "bg-red-500" },
  secondary: { hex: "#f59e0b", glow: "rgba(245,158,11,0.6)", dot: "bg-amber-500",   text: "text-amber-300",   bg: "bg-amber-500" },
  related:   { hex: "#facc15", glow: "rgba(250,204,21,0.5)", dot: "bg-yellow-500",  text: "text-yellow-300",  bg: "bg-yellow-500" },
  improved:  { hex: "#22c55e", glow: "rgba(34,197,94,0.6)",  dot: "bg-emerald-500", text: "text-emerald-300", bg: "bg-emerald-500" },
  complete:  { hex: "#22c55e", glow: "rgba(34,197,94,0.6)",  dot: "bg-emerald-500", text: "text-emerald-300", bg: "bg-emerald-500" },
  neutral:   { hex: "#64748b", glow: "rgba(100,116,139,0.4)",dot: "bg-slate-500",   text: "text-slate-300",   bg: "bg-slate-500" },
};

// ─────────────────────────────────────────────────────────────────────────
// Atoms
// ─────────────────────────────────────────────────────────────────────────
const NumBadge = memo(({ num, level = "primary" }) => {
  const c = LEVEL[level] || LEVEL.primary;
  return <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-[11px] font-semibold flex-none"
    style={{ background: c.hex, boxShadow: `0 0 10px ${c.glow}` }}>{num}</span>;
});

const Dot = memo(({ level }) => {
  const c = LEVEL[level] || LEVEL.primary;
  return <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: c.hex, boxShadow: `0 0 6px ${c.glow}` }} />;
});

const MiniStat = memo(({ label, value, delta }) => (
  <div className="bg-slate-950/60 border border-slate-800 rounded-xl px-3 py-2">
    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{label}</div>
    <div className="flex items-baseline gap-2 mt-0.5">
      <div className="text-xl text-slate-100 font-light">{value}</div>
      {delta && <div className="text-xs text-emerald-400">{delta}</div>}
    </div>
  </div>
));

// ─────────────────────────────────────────────────────────────────────────
// Anatomy figure with overlays
// ─────────────────────────────────────────────────────────────────────────
const FigureWithOverlay = memo(function FigureWithOverlay({ src, hotspots = [], showNumbers = true, onSelect, onLongPress, className = "" }) {
  // Long-press a marker -> onLongPress(id) (opens Live Capture preselected to that
  // muscle). A completed long-press suppresses the subsequent tap/select. Movement
  // beyond a small threshold (i.e. scrolling the anatomy) cancels the gesture.
  const pressTimer = React.useRef(null);
  const longFired = React.useRef(false);
  const startPt = React.useRef(null);
  const LONG_MS = 500;
  const startPress = (e, id) => {
    if (!onLongPress) return;
    longFired.current = false;
    startPt.current = { x: e.clientX, y: e.clientY };
    clearTimeout(pressTimer.current);
    pressTimer.current = setTimeout(() => { longFired.current = true; onLongPress(id); }, LONG_MS);
  };
  const movePress = (e) => {
    if (!startPt.current) return;
    if (Math.abs(e.clientX - startPt.current.x) > 10 || Math.abs(e.clientY - startPt.current.y) > 10) {
      clearTimeout(pressTimer.current);
    }
  };
  const endPress = () => { clearTimeout(pressTimer.current); startPt.current = null; };
  const clickSelect = (e, id) => {
    if (longFired.current) { e.preventDefault(); e.stopPropagation(); longFired.current = false; return; }
    onSelect?.(id);
  };
  const pressProps = (id) => (onLongPress ? {
    onPointerDown: (e) => startPress(e, id),
    onPointerMove: movePress,
    onPointerUp: endPress,
    onPointerLeave: endPress,
    onPointerCancel: endPress,
    onContextMenu: (e) => e.preventDefault(),
    style: { touchAction: "manipulation", WebkitUserSelect: "none", WebkitTouchCallout: "none" },
  } : {});
  // Numbered treatment pins can share (nearly) the same anatomical anchor.
  // We keep every glow at its TRUE anatomical location, then offset any
  // overlapping numbered badge and draw a subtle leader line back to its
  // anchor so every treatment-path number stays readable (incl. on mobile).
  const ASPECT = 1.9;      // anatomy image height/width (portrait ~1:1.9)
  const PIN_D = 12;        // 28px badge ≈ 12% of the ~240px column width
  const laidPins = useMemo(() => {
    const numbered = hotspots.filter((h) => h.num);
    const placed = [];
    const d = (a, b) => Math.hypot(a.vx - b.vx, a.vy - b.vy);
    const hits = (pt) => placed.some((p) => d(p, pt) < PIN_D * 0.85);
    const ring = [-90, -50, -130, -20, -160, 35, 145, 90];
    return numbered.map((h) => {
      const anchor = { vx: h.x, vy: h.y * ASPECT };
      if (!hits(anchor)) { placed.push(anchor); return { ...h, dispX: h.x, dispY: h.y, offset: false }; }
      for (const R of [PIN_D, PIN_D * 1.75, PIN_D * 2.6]) {
        for (const deg of ring) {
          const rad = (deg * Math.PI) / 180;
          const pt = { vx: anchor.vx + R * Math.cos(rad), vy: anchor.vy + R * Math.sin(rad) };
          if (pt.vx < 5 || pt.vx > 95) continue;
          if (!hits(pt)) { placed.push(pt); return { ...h, dispX: pt.vx, dispY: pt.vy / ASPECT, offset: true }; }
        }
      }
      const pt = { vx: anchor.vx, vy: anchor.vy - PIN_D * 3 };
      placed.push(pt);
      return { ...h, dispX: pt.vx, dispY: pt.vy / ASPECT, offset: true };
    });
  }, [hotspots]);

  return (
    <div className={`relative ${className}`} data-testid="anatomy-figure">
      <img src={src} alt="anatomical figure" loading="lazy" decoding="async" className="block w-full h-auto select-none pointer-events-none" />
      {hotspots.map((h, i) => {
        const c = LEVEL[h.level] || LEVEL.primary;
        return (
          <button
            key={`hs-${h.id}-${i}`}
            data-testid={`hotspot-${h.id}`}
            onClick={(e) => clickSelect(e, h.id)}
            {...pressProps(h.id)}
            className="absolute rounded-full focus:outline-none"
            style={{
              left: `${h.x}%`, top: `${h.y}%`,
              width: `${h.size * 2.6}%`, height: `${h.size * 2.6}%`,
              transform: "translate(-50%,-50%)",
              background: `radial-gradient(circle at 50% 50%, ${c.glow} 0%, ${c.glow} 38%, rgba(0,0,0,0) 72%)`,
              mixBlendMode: "screen",
              cursor: "pointer", border: "none",
              ...(pressProps(h.id).style || {}),
            }}
            title={h.label || h.id}
            aria-label={h.label || h.id}
          />
        );
      })}
      {showNumbers && (
        <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ overflow: "visible" }} aria-hidden="true">
          {laidPins.filter((h) => h.offset).map((h, i) => {
            const c = LEVEL[h.level] || LEVEL.primary;
            return (
              <g key={`ldr-${h.id}-${i}`}>
                <line x1={`${h.x}%`} y1={`${h.y}%`} x2={`${h.dispX}%`} y2={`${h.dispY}%`} stroke={c.hex} strokeWidth="1.5" strokeLinecap="round" opacity="0.9" />
                <circle cx={`${h.x}%`} cy={`${h.y}%`} r="3" fill={c.hex} stroke="rgba(0,0,0,0.5)" strokeWidth="1" />
              </g>
            );
          })}
        </svg>
      )}
      {showNumbers && laidPins.map((h, i) => {
        const c = LEVEL[h.level] || LEVEL.primary;
        return (
          <button
            key={`pin-${h.id}-${i}`}
            data-testid={`pin-${h.id}`}
            onClick={(e) => clickSelect(e, h.id)}
            {...pressProps(h.id)}
            className="absolute flex items-center justify-center text-white text-xs font-semibold rounded-full ring-2 ring-black/40 focus:outline-none focus:ring-2 focus:ring-white"
            style={{
              left: `${h.dispX}%`, top: `${h.dispY}%`,
              width: 28, height: 28, transform: "translate(-50%,-50%)",
              background: c.hex, boxShadow: `0 0 14px ${c.glow}`, cursor: "pointer", border: "none",
              ...(pressProps(h.id).style || {}),
            }}
          >
            {h.num}
          </button>
        );
      })}
    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────────
// Today's Treatment Focus hero (sticky on mobile)
// ─────────────────────────────────────────────────────────────────────────
const TodaysFocusHero = memo(function TodaysFocusHero({ steps, totalTime, expectedResult, sessionState, onStart, onPause, mode }) {
  const isClient = mode === "client";
  const inProgress = sessionState === "in_progress";
  return (
    <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-900 to-violet-950/40 border border-violet-900/40 p-4 sm:p-5 mb-4 lg:sticky lg:top-[60px] z-20 backdrop-blur" data-testid="todays-focus-hero">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex-1 min-w-[240px]">
          <div className="text-[10px] uppercase tracking-[0.22em] text-violet-300 mb-1.5 inline-flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" />
            {isClient ? "What we're working on today" : inProgress ? "Session in progress · live route" : "Today's treatment focus"}
          </div>
          <div className="flex items-center flex-wrap gap-2 mt-1">
            {steps.map((s) => {
              const done = s.addressed;
              return (
                <span
                  key={s.num}
                  className={`inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border transition ${
                    done ? "bg-emerald-500/15 border-emerald-700/50 text-emerald-200" : "bg-slate-900/80 border-slate-700 text-slate-100"
                  }`}
                  data-testid={`hero-step-${s.num}`}
                >
                  {done ? <Check className="w-4 h-4 text-emerald-400" /> : <NumBadge num={s.num} level={s.level} />}
                  <span className="text-sm font-medium">{s.label}</span>
                </span>
              );
            })}
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-400 ml-1"><Timer className="w-3.5 h-3.5" /> ~{totalTime} min</span>
          </div>
          <p className="text-sm text-slate-300 mt-3 leading-relaxed max-w-2xl">
            {isClient
              ? "Expected today: calmer neck, easier turning, less shoulder lift, better posture."
              : <><span className="text-slate-400">Expected result · </span>{expectedResult}</>}
          </p>
        </div>
        {!isClient && (
          inProgress ? (
            <button onClick={onPause} className="px-5 py-3 rounded-2xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold inline-flex items-center gap-2 shadow-xl" data-testid="hero-pause-btn">
              <PauseCircle className="w-4 h-4" /> Pause session
            </button>
          ) : (
            <button onClick={onStart} className="px-6 py-3.5 rounded-2xl bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white font-semibold inline-flex items-center gap-2 shadow-xl shadow-violet-500/30 text-base" data-testid="hero-start-btn">
              <Play className="w-4 h-4 fill-current" /> Start Treatment Plan
            </button>
          )
        )}
      </div>
    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────────
// Anatomy column (only the active view image is mounted → lazy load)
// ─────────────────────────────────────────────────────────────────────────
const AnatomyColumn = memo(function AnatomyColumn({ sex, view, hotspots, onSelect, onLongPress }) {
  const sk = sexKey(sex);
  const src = ANATOMY[sk][view] || ANATOMY[sk].front;
  return (
    <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-3 sm:p-4" data-testid="anatomy-column">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
          Body · {view === "side_l" ? "Left lateral" : view === "side_r" ? "Right lateral" : view}
        </div>
        <div className="flex items-center gap-2 text-[10px] text-slate-400 flex-wrap">
          <span className="inline-flex items-center gap-1"><Dot level="primary" />Primary</span>
          <span className="inline-flex items-center gap-1"><Dot level="secondary" />Secondary</span>
          <span className="inline-flex items-center gap-1"><Dot level="related" />Related</span>
        </div>
      </div>
      <FigureWithOverlay src={src} hotspots={hotspots} onSelect={onSelect} onLongPress={onLongPress} className="max-w-[240px] mx-auto" />
      <div className="text-[11px] text-slate-500 text-center mt-2 inline-flex items-center gap-1 justify-center w-full">
        <Info className="w-3 h-3" /> Tap any highlight for the finding
      </div>
    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────────
// Treatment Path (workflow) — therapist mode
// ─────────────────────────────────────────────────────────────────────────
const TreatmentPath = memo(function TreatmentPath({ steps, activeNum, sessionState, onSelect, onToggleAddressed }) {
  return (
    <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-1">
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Treatment path</div>
        <div className="text-[10px] text-slate-500">{steps.reduce((a, s) => a + (parseInt(s.time, 10) || 0), 0)} min total</div>
      </div>
      <ol className="relative space-y-2 before:absolute before:left-[15px] before:top-2 before:bottom-2 before:w-px before:bg-slate-800">
        {steps.map((t, idx) => {
          const isActive = sessionState === "in_progress" && t.num === activeNum;
          const isDone = t.addressed;
          const showPos = t.position && t.position !== steps[idx - 1]?.position;
          return (
            <React.Fragment key={t.num}>
              {showPos && (
                <li className="relative pl-11 pt-1" data-testid={`path-position-${String(t.position).toLowerCase()}`}>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-sky-300/80 font-medium">{t.position}</span>
                </li>
              )}
              <li className="relative pl-11">
              <span className="absolute left-0 top-2.5">
                {isDone ? <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500 text-white"><Check className="w-3.5 h-3.5" /></span> : <NumBadge num={t.num} level={t.level} />}
              </span>
              <div className={`rounded-xl px-3 py-2 transition flex items-center justify-between gap-2 border ${
                isActive ? "border-violet-500/70 bg-violet-500/10 ring-1 ring-violet-500/40 shadow-lg shadow-violet-500/10" :
                isDone   ? "border-emerald-900/50 bg-emerald-500/5" :
                          "border-slate-800 bg-slate-900/60 hover:bg-slate-900"
              }`}>
                <button onClick={() => t.key && onSelect?.(t.key)} className="flex-1 min-w-0 text-left" data-testid={`path-step-${t.num}`}>
                  <div className="flex items-center gap-2">
                    <div className={`text-sm truncate font-medium ${isDone ? "text-emerald-200 line-through opacity-70" : "text-slate-100"}`}>{t.label}</div>
                    <div className="text-xs text-slate-500">· {t.time}</div>
                    {isActive && <span className="text-[10px] uppercase tracking-wider text-violet-300 px-1.5 py-0.5 rounded-full bg-violet-500/20 ring-1 ring-violet-500/40">Active</span>}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5 truncate">{t.goal}</div>
                  {t.intervention && (
                    <div className="text-xs text-sky-300/90 mt-1 flex items-start gap-1" data-testid={`path-step-intervention-${t.num}`}>
                      <Info className="w-3 h-3 mt-0.5 shrink-0" />
                      <span>{t.intervention}</span>
                    </div>
                  )}
                  {t.positionWhy && (
                    <div className="text-[11px] text-slate-400 mt-1" data-testid={`path-step-position-why-${t.num}`}>
                      Why {t.position}: {t.positionWhy}
                      {t.positionAlt && <span className="text-slate-500"> · Alternative: {t.positionAlt}</span>}
                    </div>
                  )}
                </button>
                <button
                  onClick={() => onToggleAddressed?.(t.num)}
                  data-testid={`addressed-${t.num}`}
                  className={`flex-none p-1.5 rounded-full transition ${isDone ? "bg-emerald-500/30 text-emerald-200" : "bg-slate-800 text-slate-500 hover:text-slate-200"}`}
                  title={isDone ? "Mark not done" : "Mark addressed"}
                >
                  <Check className="w-4 h-4" />
                </button>
              </div>
            </li>
            </React.Fragment>
          );
        })}
      </ol>
    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────────
// AI reasoning / confidence (therapist only)
// ─────────────────────────────────────────────────────────────────────────
const WhyThisOrder = memo(function WhyThisOrder({ rationale, confidence, basis }) {
  const pct = Math.round((confidence || 0) * 100);
  return (
    <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-4">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-violet-400 mb-2">
        <Sparkles className="w-3.5 h-3.5" /> Why this order?
      </div>
      <p className="text-sm text-slate-200 leading-relaxed">{rationale}</p>
      <div className="mt-3 flex items-center gap-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">AI confidence</div>
        <div className="flex-1 h-1.5 rounded-full bg-slate-800 overflow-hidden"><div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-300" style={{ width: `${pct}%` }} /></div>
        <div className="text-xs text-emerald-300 font-medium">{pct}%</div>
      </div>
      <div className="text-[11px] text-slate-500 mt-1.5">{basis}</div>
    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────────
// "New Since Last Visit" panel
// ─────────────────────────────────────────────────────────────────────────
const NewSinceLastVisit = memo(function NewSinceLastVisit({ data }) {
  if (!data) return null;
  const sections = [
    { key: "improved",       label: "Improved",          tone: "improved",  icon: <TrendingDown className="w-3.5 h-3.5" /> },
    { key: "stable",         label: "Stable",            tone: "secondary", icon: <PauseCircle className="w-3.5 h-3.5" /> },
    { key: "worse",          label: "Worse / new",       tone: "primary",   icon: <TrendingUp className="w-3.5 h-3.5" /> },
    { key: "clientReported", label: "Client reported",   tone: "neutral",   icon: <User className="w-3.5 h-3.5" /> },
    { key: "romChanges",     label: "ROM changes",       tone: "neutral",   icon: <Activity className="w-3.5 h-3.5" /> },
  ];
  return (
    <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-4" data-testid="new-since-last-visit">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3 inline-flex items-center gap-1.5">
        <Sparkles className="w-3.5 h-3.5 text-sky-400" /> New since last visit
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
        {sections.map((sec) => {
          const items = data[sec.key] || [];
          if (items.length === 0) return null;
          const c = LEVEL[sec.tone] || LEVEL.neutral;
          return (
            <div key={sec.key} className="bg-slate-950/60 border border-slate-800 rounded-xl p-3">
              <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] ${c.text} mb-2`}>{sec.icon}{sec.label}</div>
              <ul className="space-y-1.5">
                {items.map((it, i) => (
                  <li key={i} className="text-xs text-slate-200 flex items-start gap-1.5 leading-snug">
                    <span className={`mt-1.5 inline-block w-1.5 h-1.5 rounded-full ${c.dot}`} />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────────
// Previous Session Findings rail — collapsible on mobile
// ─────────────────────────────────────────────────────────────────────────
const trendStatus = (history) => {
  if (!history?.length) return { label: "Stable", level: "secondary", icon: "■" };
  const first = history[0];
  const last = history[history.length - 1];
  if (last <= first - 2) return { label: "Improved", level: "improved", icon: "▼" };
  if (last >= first + 1) return { label: "Worse",    level: "primary",  icon: "▲" };
  return { label: "Stable", level: "secondary", icon: "■" };
};

const PreviousSessionFindings = memo(function PreviousSessionFindings({ findings, onSelect }) {
  const [expanded, setExpanded] = useState(findings[0]?.id || null);
  const [collapsedMobile, setCollapsedMobile] = useState(false);
  if (!findings?.length) return null;
  return (
    <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-4" data-testid="prev-findings-rail">
      <button
        onClick={() => setCollapsedMobile((v) => !v)}
        className="w-full flex items-center justify-between gap-1.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3 lg:cursor-default"
        data-testid="prev-findings-collapse"
      >
        <span className="inline-flex items-center gap-1.5"><Activity className="w-3.5 h-3.5 text-sky-400" /> Previous session findings</span>
        <ChevronDown className={`w-3.5 h-3.5 transition lg:hidden ${collapsedMobile ? "" : "rotate-180"}`} />
      </button>
      <div className={`space-y-2 ${collapsedMobile ? "hidden lg:block" : "block"}`}>
        {findings.map((f) => {
          const status = trendStatus(f.history);
          const isOpen = expanded === f.id;
          return (
            <div key={f.id} className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden" data-testid={`prev-finding-${f.id}`}>
              <button onClick={() => setExpanded(isOpen ? null : f.id)} className="w-full text-left p-3 hover:bg-slate-900 transition">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="text-sm text-slate-100 truncate flex-1">{f.label}</div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${LEVEL[status.level].text} border border-current/30 inline-flex items-center gap-1`} style={{ background: LEVEL[status.level].glow.replace("0.6", "0.15").replace("0.5", "0.15") }}>
                    <span className="text-[8px]">{status.icon}</span>{status.label}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {f.history.map((v, i) => {
                    const t = v >= 7 ? "primary" : v >= 4 ? "secondary" : v >= 2 ? "related" : "improved";
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                        <span className={`block w-full h-1.5 rounded-full ${LEVEL[t].dot}`} />
                        <span className="text-[9px] text-slate-500">S{i + 1}</span>
                      </div>
                    );
                  })}
                </div>
              </button>
              {isOpen && f.detail?.perSession?.length > 0 && (
                <div className="border-t border-slate-800 bg-slate-950/60 p-3 space-y-1.5">
                  {f.detail.perSession.slice(-3).map((s) => (
                    <div key={s.num} className="text-[11px] text-slate-300 flex items-start gap-2">
                      <span className="text-slate-500 font-medium w-6 flex-none">S{s.num}</span>
                      <span className="flex-1 leading-snug">
                        <span className="text-slate-400">Pain {s.pain}/10{s.rom ? ` · ROM ${s.rom}°` : ""} — </span>{s.note}
                      </span>
                    </div>
                  ))}
                  <button onClick={() => onSelect?.(f.id)} className="mt-2 w-full text-xs text-violet-300 hover:text-violet-200 inline-flex items-center justify-center gap-1.5">
                    Open clinical card <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────────
// Treatment Logic — therapist's brain
// ─────────────────────────────────────────────────────────────────────────
const TreatmentLogicPanel = memo(function TreatmentLogicPanel({ logic }) {
  if (!logic) return null;
  const rows = [
    { tone: "rose",    label: "Primary Complaint", body: logic.primaryComplaint, helper: "Chief complaint this visit" },
    { tone: "amber",   label: "Chronic Priority",  body: logic.chronicPriority,  helper: "Recurring across sessions" },
    { tone: "sky",     label: "New Finding",       body: logic.newFinding,       helper: "First flagged this session" },
    { tone: "violet",  label: "Compensation",      body: logic.compensation,     helper: "Driven by primary pattern" },
  ];
  const toneCls = {
    rose:   { wrap: "from-rose-950/40 border-rose-900/40",       title: "text-rose-300" },
    amber:  { wrap: "from-amber-950/40 border-amber-900/40",     title: "text-amber-300" },
    sky:    { wrap: "from-sky-950/40 border-sky-900/40",         title: "text-sky-300" },
    violet: { wrap: "from-violet-950/40 border-violet-900/40",   title: "text-violet-300" },
  };
  return (
    <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-4" data-testid="treatment-logic-panel">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">
        <Sparkles className="w-3.5 h-3.5 text-violet-400" /> Treatment logic
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {rows.map((r) => (
          <div key={r.label} className={`rounded-2xl bg-gradient-to-br ${toneCls[r.tone].wrap} via-slate-900 to-slate-900 border p-3.5`}>
            <div className={`text-[10px] uppercase tracking-[0.18em] ${toneCls[r.tone].title} mb-1`}>{r.label}</div>
            <div className="text-sm text-slate-100 leading-snug">{r.body || "—"}</div>
            <div className="text-[11px] text-slate-500 mt-1.5">{r.helper}</div>
          </div>
        ))}
      </div>
    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────────
// Finding detail drawer (click hotspot → opens)
// ─────────────────────────────────────────────────────────────────────────
const FindingDrawer = memo(function FindingDrawer({ finding, onClose, onAddToPlan, onMarkAddressed }) {
  if (!finding) return null;
  const c = LEVEL[finding.level] || LEVEL.primary;
  const history = finding.history || [];
  return (
    <div className="fixed inset-0 z-50" data-testid="finding-drawer">
      <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" onClick={onClose} />
      <aside className="absolute right-0 top-0 bottom-0 w-full sm:max-w-md bg-slate-950 border-l border-slate-800 shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-slate-950/95 backdrop-blur border-b border-slate-800 p-5 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-slate-500">
              <span className={`inline-block w-2 h-2 rounded-full ${c.dot}`} />{finding.level} finding
            </div>
            <h3 className="text-xl text-slate-100 font-light mt-1">{finding.label}</h3>
            {finding.treatmentPriority && <div className="text-xs text-slate-500 mt-0.5">Treatment priority · #{finding.treatmentPriority}</div>}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-100" data-testid="close-drawer"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-5 text-sm">
          {finding.why && (
            <section>
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2"><Sparkles className="w-3.5 h-3.5 text-violet-400" /> Why it was flagged</div>
              <p className="text-slate-300 leading-relaxed">{finding.why}</p>
              {finding.confidence != null && (
                <div className="mt-3 flex items-center gap-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">AI confidence</div>
                  <div className="flex-1 h-1.5 rounded-full bg-slate-800 overflow-hidden"><div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-300" style={{ width: `${Math.round(finding.confidence * 100)}%` }} /></div>
                  <div className="text-xs text-emerald-300 font-medium">{Math.round(finding.confidence * 100)}%</div>
                </div>
              )}
            </section>
          )}
          {finding.sequence?.length > 0 && (
            <section>
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2"><Activity className="w-3.5 h-3.5 text-amber-400" /> Recommended sequence</div>
              <ol className="space-y-2">
                {finding.sequence.map((s, i) => (
                  <li key={i} className="flex items-center gap-2.5">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-800 text-[10px] text-slate-300 font-medium">{i + 1}</span>
                    <span className="text-slate-200">{s}</span>
                  </li>
                ))}
              </ol>
            </section>
          )}
          {finding.expectedOutcome && (
            <section>
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2"><Target className="w-3.5 h-3.5 text-emerald-400" /> Expected outcome</div>
              <p className="text-slate-300 leading-relaxed">{finding.expectedOutcome}</p>
            </section>
          )}
          {history.length > 0 && <SessionTrend history={history} />}
        </div>
        <div className="sticky bottom-0 bg-slate-950/95 backdrop-blur border-t border-slate-800 p-4 flex gap-2">
          <button onClick={() => onAddToPlan?.(finding.id)} className="flex-1 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium" data-testid="drawer-add-to-plan">Add to plan</button>
          <button onClick={() => { onMarkAddressed?.(finding.id); onClose?.(); }} className="flex-1 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm" data-testid="drawer-mark-addressed">Mark addressed</button>
        </div>
      </aside>
    </div>
  );
});

function SessionTrend({ history }) {
  const W = 320, H = 70, P = 8;
  const xs = history.map((_, i) => P + (i * (W - 2 * P)) / Math.max(history.length - 1, 1));
  const ys = history.map((v) => H - P - (v / 10) * (H - 2 * P));
  const path = history.map((_, i) => `${i === 0 ? "M" : "L"} ${xs[i]} ${ys[i]}`).join(" ");
  const last = history[history.length - 1];
  const stroke = last >= 7 ? "#ef4444" : last >= 4 ? "#f59e0b" : last >= 2 ? "#facc15" : "#22c55e";
  return (
    <section>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2"><TrendingDown className="w-3.5 h-3.5 text-sky-400" /> Trend across sessions</div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
        <path d={path} stroke={stroke} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        {ys.map((y, i) => <circle key={i} cx={xs[i]} cy={y} r="3.5" fill={stroke} stroke="#0f172a" strokeWidth="1.5" />)}
        {xs.map((x, i) => <text key={i} x={x} y={H - 1} fill="#475569" fontSize="8" textAnchor="middle">S{i + 1}</text>)}
      </svg>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Client / Learning mode panels
// ─────────────────────────────────────────────────────────────────────────
const ClientWorkflow = memo(function ClientWorkflow({ progressPct, plainSteps, onSelect }) {
  return (
    <div className="space-y-3" data-testid="client-workflow">
      <div className="rounded-2xl bg-gradient-to-br from-emerald-950/40 via-slate-900 to-slate-900 border border-emerald-900/40 p-5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 mb-1">Your progress</div>
        <h3 className="text-2xl font-light text-slate-100">You&apos;re {progressPct}% to your goal</h3>
        <p className="text-sm text-slate-300 mt-1.5">Steady improvement across the board. Keep it up.</p>
        <div className="h-2 rounded-full bg-slate-800 overflow-hidden mt-4"><div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-300" style={{ width: `${progressPct}%` }} /></div>
      </div>
      <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-4">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Today&apos;s focus</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {plainSteps.map((c, i) => (
            <button key={i} onClick={() => c.key && onSelect?.(c.key)} className="text-left bg-slate-900/60 hover:bg-slate-900 border border-slate-800 rounded-xl p-3.5 transition" data-testid={`client-focus-${i}`}>
              <div className="flex items-start gap-2.5">
                <div className="text-xl">{c.icon}</div>
                <div className="min-w-0">
                  <div className="text-sm text-slate-100 leading-tight">{c.label}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{c.desc}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
});

const LearningWorkflow = memo(function LearningWorkflow({ selectedMuscle, library, onSelect }) {
  const m = library?.[selectedMuscle] || library?.upper_trap;
  if (!m) return <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-4 text-sm text-slate-400">Tap a muscle on the body to learn more.</div>;
  return (
    <div className="space-y-3" data-testid="learning-workflow">
      <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-4">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-fuchsia-300 mb-2"><BookOpen className="w-3.5 h-3.5" /> Learning notes</div>
        <h3 className="text-xl font-light text-slate-100">{m.label}</h3>
        {m.latin && <div className="text-xs italic text-slate-400">{m.latin}</div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-4">
          {m.origin     && <StudyCard title="Origin" body={m.origin} />}
          {m.insertion  && <StudyCard title="Insertion" body={m.insertion} />}
          {m.action     && <StudyCard title="Action" body={m.action} />}
          {m.innervation&& <StudyCard title="Innervation" body={m.innervation} />}
        </div>
        {m.referral && (
          <div className="mt-4 p-3 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-xs text-amber-200">
            <div className="font-semibold mb-1">Referral pattern</div>{m.referral}
          </div>
        )}
      </div>
      {m.relatedMuscles && (
        <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Related muscles in this chain</div>
          <div className="flex flex-wrap gap-2">
            {m.relatedMuscles.map((rm) => <span key={rm} className="px-2.5 py-1 rounded-full bg-slate-900/60 border border-slate-800 text-xs text-slate-300">{rm}</span>)}
          </div>
        </div>
      )}
      <div className="flex gap-2">
        <button className="flex-1 py-2.5 rounded-xl bg-fuchsia-600 hover:bg-fuchsia-500 text-white text-sm font-medium">Quiz me</button>
        <button onClick={() => onSelect?.(selectedMuscle)} className="flex-1 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm">See clinical card</button>
      </div>
    </div>
  );
});
const StudyCard = memo(function StudyCard({ title, body }) {
  return (
    <div className="rounded-xl bg-slate-950/60 border border-slate-800 px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{title}</div>
      <div className="text-sm text-slate-200 leading-snug">{body}</div>
    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────────
// Top header (client + mode/sex/view toggles + stats)
// ─────────────────────────────────────────────────────────────────────────
const TopHeader = memo(function TopHeader({ client, sessionMeta, mode, setMode, sex, setSex, view, setView, stats, onCaptureClick, captureActive }) {
  const initials = (client?.name || "").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase() || "—";
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-4 mb-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 ring-2 ring-slate-700 flex items-center justify-center text-slate-300 font-medium text-sm">{initials}</div>
          <div>
            <div className="text-slate-100 font-medium leading-tight">{client?.name || "Client"}</div>
            <div className="text-xs text-slate-500">Session {sessionMeta?.num ?? "—"}{sessionMeta?.totalSessions ? ` of ${sessionMeta.totalSessions}` : ""}{sessionMeta?.date ? ` · ${sessionMeta.date}` : ""}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {onCaptureClick && (
            <button
              type="button"
              onClick={onCaptureClick}
              data-testid="workspace-capture-btn"
              className={`inline-flex items-center gap-2 px-4 min-h-[44px] rounded-full text-sm font-medium transition border ${
                captureActive
                  ? "bg-rose-500/20 border-rose-500/40 text-rose-200 ring-2 ring-rose-500/30 animate-pulse"
                  : "bg-violet-500/15 border-violet-500/40 text-violet-200 hover:bg-violet-500/25"
              }`}
              title={captureActive ? "Recording — tap to stop" : "Dictate a finding"}
            >
              <span className="relative inline-flex w-2.5 h-2.5">
                {captureActive && <span className="absolute inset-0 rounded-full bg-rose-400 animate-ping opacity-70" />}
                <span className={`relative w-2.5 h-2.5 rounded-full ${captureActive ? "bg-rose-400" : "bg-violet-400"}`} />
              </span>
              {captureActive ? "Recording…" : "Capture finding"}
            </button>
          )}
          <div className="inline-flex rounded-full bg-slate-900 border border-slate-800 p-0.5 text-xs" data-testid="sex-toggle">
            {[{ id: "f", label: "Female" }, { id: "m", label: "Male" }].map((s) => (
              <button key={s.id} onClick={() => setSex(s.id)}
                className={`px-3 min-h-[40px] rounded-full font-medium transition ${sexKey(sex) === s.id ? "bg-slate-100 text-slate-900" : "text-slate-400 hover:text-slate-100"}`}
                data-testid={`sex-${s.id}`}>{s.label}</button>
            ))}
          </div>
          <div className="inline-flex rounded-full bg-slate-900 border border-slate-800 p-0.5 text-xs" data-testid="mode-toggle">
            {[
              { id: "therapist", label: "Therapist", icon: <Stethoscope className="w-3.5 h-3.5" /> },
              { id: "client",    label: "Client",    icon: <User className="w-3.5 h-3.5" /> },
              { id: "learning",  label: "Learning",  icon: <GraduationCap className="w-3.5 h-3.5" /> },
            ].map((m) => (
              <button key={m.id} onClick={() => setMode(m.id)}
                className={`px-3 min-h-[40px] rounded-full font-medium inline-flex items-center gap-1.5 transition ${mode === m.id ? "bg-slate-100 text-slate-900" : "text-slate-400 hover:text-slate-100"}`}
                data-testid={`mode-${m.id}`}>{m.icon}<span className="hidden sm:inline">{m.label}</span></button>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 items-end">
        <div className="grid grid-cols-3 gap-3">
          <MiniStat label="Pain"     value={stats?.pain?.current   != null ? `${stats.pain.current}/10` : "—"}   delta={stats?.pain?.delta != null ? `${stats.pain.delta}` : ""} />
          <MiniStat label="Neck ROM" value={stats?.rom?.current    != null ? `${stats.rom.current}°`   : "—"}   delta={stats?.rom?.delta  != null ? `+${stats.rom.delta}°` : ""} />
          <MiniStat label="Posture"  value={stats?.posture?.current!= null ? `${stats.posture.current}%`: "—"}   delta={stats?.posture?.delta != null ? `+${stats.posture.delta}%` : ""} />
        </div>
        <div className="inline-flex rounded-full bg-slate-900 border border-slate-800 p-0.5 text-xs self-start lg:self-end" data-testid="view-tabs">
          {[{ id: "front", label: "Front" }, { id: "back", label: "Back" }, { id: "side_l", label: "Side L" }, { id: "side_r", label: "Side R" }].map((v) => (
            <button key={v.id} onClick={() => setView(v.id)}
              className={`px-3 min-h-[40px] rounded-full font-medium transition ${view === v.id ? "bg-slate-100 text-slate-900" : "text-slate-400 hover:text-slate-100"}`}
              data-testid={`view-${v.id}`}>{v.label}</button>
          ))}
        </div>
      </div>
    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────────
// Main exported component
// ─────────────────────────────────────────────────────────────────────────
const MODE_KEY = "posturra.workspaceMode";

export default function ClinicalWorkspace({
  client = { name: "Client", sex: "unspecified" },
  sessionMeta = { num: 1, totalSessions: null, date: null },
  stats = {},
  treatmentMap,
  treatmentSteps = [],
  treatmentRationale,
  treatmentConfidence = 0,
  treatmentBasis = "",
  expectedResult = "",
  previousFindings = [],
  newSinceLastVisit = null,
  treatmentLogic = null,
  findingsLibrary = {},      // muscle_id -> finding detail
  learningLibrary = {},      // muscle_id -> { label, latin, origin, ... }
  clientPlainSteps = [],
  progressPct = 0,
  recentCaptures = [],       // newest-first list of saved captures shown in the workspace
  onStartPlan,
  onMuscleAddressed,
  onMuscleAddToPlan,
  onCaptureClick,            // emits when user taps the inline "Capture finding" button
  captureActive = false,     // shows recording pulse on the inline button
  onMuscleLongPress,         // long-press a body marker -> open Live Capture preselected to that muscle
}) {
  // Mode persistence (Clinical vs Learning continuity from old DiagramSection)
  const [mode, setMode] = useState(() => {
    if (typeof window === "undefined") return "therapist";
    return window.localStorage.getItem(MODE_KEY) || "therapist";
  });
  const setModePersisted = useCallback((m) => {
    setMode(m);
    if (typeof window !== "undefined") window.localStorage.setItem(MODE_KEY, m);
  }, []);

  const [sex, setSex]     = useState(client.sex || "unspecified");
  const [view, setView]   = useState("back");
  const [sessionState, setSessionState] = useState("idle"); // idle | in_progress
  const [openId, setOpenId]             = useState(null);
  const [addressed, setAddressed]       = useState(() => new Set());
  const [activeStep, setActiveStep]     = useState(1);

  const onSelectMuscle = useCallback((id) => {
    if (findingsLibrary?.[id]) setOpenId(id);
  }, [findingsLibrary]);

  const onToggleAddressed = useCallback((stepNum) => {
    setAddressed((prev) => {
      const next = new Set(prev);
      if (next.has(stepNum)) next.delete(stepNum); else next.add(stepNum);
      return next;
    });
    const step = treatmentSteps.find((s) => s.num === stepNum);
    if (step && onMuscleAddressed) onMuscleAddressed(step.key);
  }, [treatmentSteps, onMuscleAddressed]);

  const startSession = useCallback(() => {
    setSessionState("in_progress");
    setActiveStep(treatmentSteps[0]?.num || 1);
    onStartPlan?.();
  }, [treatmentSteps, onStartPlan]);

  const pauseSession = useCallback(() => setSessionState("idle"), []);

  // Compute hotspots for the active view from treatment map + analysis
  const hotspots = useMemo(() => {
    if (!treatmentMap) return [];
    const items = [];
    const isLateral = view === "side_l" || view === "side_r";
    const push = (ids, level) => (ids || []).forEach((id) => {
      const stepNum = treatmentSteps.findIndex((s) => s.key === id) + 1;
      const num = stepNum > 0 ? stepNum : undefined;
      const label = findingsLibrary?.[id]?.label || id;
      if (isLateral) {
        const lp = lateralPos(id, view);
        if (lp) items.push({ id, level, x: lp.x, y: lp.y, size: lp.size, label, num });
        return;
      }
      const pos = MUSCLE_POSITION_MAP[id];
      if (!pos || pos.view !== view) return;
      items.push({ id, level, x: pos.x, y: pos.y, size: pos.size, label, num });
    });
    push(treatmentMap.primary,   "primary");
    push(treatmentMap.secondary, "secondary");
    push(treatmentMap.related,   "related");
    return items;
  }, [treatmentMap, treatmentSteps, view, findingsLibrary]);

  const totalTime = useMemo(() => treatmentSteps.reduce((a, s) => a + (parseInt(s.time, 10) || 0), 0), [treatmentSteps]);

  const heroSteps = useMemo(() => treatmentSteps.filter((s) => s.key && s.key !== "_reassess").slice(0, 4).map((s) => ({ ...s, addressed: addressed.has(s.num) })), [treatmentSteps, addressed]);
  const pathSteps = useMemo(() => treatmentSteps.map((s) => ({ ...s, addressed: addressed.has(s.num) })), [treatmentSteps, addressed]);

  const openFinding = openId ? { id: openId, ...findingsLibrary[openId] } : null;

  return (
    <div className="text-slate-100" data-testid="clinical-workspace">
      <TopHeader client={client} sessionMeta={sessionMeta} mode={mode} setMode={setModePersisted} sex={sex} setSex={setSex} view={view} setView={setView} stats={stats} onCaptureClick={onCaptureClick} captureActive={captureActive} />

      <TodaysFocusHero
        steps={heroSteps}
        totalTime={totalTime}
        expectedResult={expectedResult}
        sessionState={sessionState}
        onStart={startSession}
        onPause={pauseSession}
        mode={mode}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(220px,260px)_minmax(0,1fr)_minmax(280px,360px)] gap-4">
        <AnatomyColumn sex={sex} view={view} hotspots={hotspots} onSelect={onSelectMuscle} onLongPress={onMuscleLongPress} />

        {mode === "therapist" && (
          <div className="space-y-3">
            <TreatmentPath steps={pathSteps} activeNum={activeStep} sessionState={sessionState} onSelect={onSelectMuscle} onToggleAddressed={onToggleAddressed} />
            {treatmentRationale && <WhyThisOrder rationale={treatmentRationale} confidence={treatmentConfidence} basis={treatmentBasis} />}
            {newSinceLastVisit && <NewSinceLastVisit data={newSinceLastVisit} />}
          </div>
        )}
        {mode === "client" && <ClientWorkflow progressPct={progressPct} plainSteps={clientPlainSteps} onSelect={onSelectMuscle} />}
        {mode === "learning" && <LearningWorkflow selectedMuscle={openId || "upper_trap"} library={learningLibrary} onSelect={onSelectMuscle} />}

        <PreviousSessionFindings findings={previousFindings} onSelect={onSelectMuscle} />
      </div>

      {mode === "therapist" && treatmentLogic && (
        <div className="mt-4">
          <TreatmentLogicPanel logic={treatmentLogic} />
        </div>
      )}

      {mode === "therapist" && recentCaptures.length > 0 && (
        <div className="mt-4 bg-slate-900/40 border border-slate-800 rounded-2xl p-4" data-testid="recent-captures">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 inline-flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-emerald-400" /> Today’s live captures · {recentCaptures.length}
            </div>
          </div>
          <ul className="space-y-2">
            {recentCaptures.slice(0, 6).map((c, i) => {
              const region = `${c.side ? `${c.side[0].toUpperCase() + c.side.slice(1)} ` : ""}${c.body_region || "—"}`;
              return (
                <li key={c.id || i} className="bg-slate-950/60 border border-slate-800 rounded-xl px-3 py-2 text-sm flex items-start gap-3" data-testid={`recent-capture-${i}`}>
                  <span className="mt-1 w-2 h-2 rounded-full bg-emerald-500 flex-none" />
                  <div className="min-w-0 flex-1">
                    <div className="text-slate-100 truncate">{region}{c.tissue_quality ? ` · ${c.tissue_quality}` : ""}{c.severity != null ? ` · ${c.severity}/10` : ""}</div>
                    {c.client_response && <div className="text-xs text-slate-400 truncate">→ {c.client_response}</div>}
                  </div>
                  {c.added_to_longitudinal && (
                    <span className="text-[10px] uppercase tracking-wider text-emerald-300 flex-none">Longitudinal</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <FindingDrawer
        finding={openFinding}
        onClose={() => setOpenId(null)}
        onAddToPlan={onMuscleAddToPlan}
        onMarkAddressed={onMuscleAddressed}
      />
    </div>
  );
}
